import { browserTracingIntegration, type ErrorEvent } from '@sentry/react';
import env from '@frontend/env';
import { tagThirdPartyEvent, tagThirdPartySpan, getSentryConfig as getBaseSentryConfig } from '@frontend/tracking';

/**
 * Array of regex patterns to ignore for error capturing.
 * DFE only ignores auth-related HTTP errors — other noise is minimal.
 */
const ignorePatterns: (RegExp | string)[] = [/status code 40[134]/gim];

export function getSentryConfig() {
  // IMPORTANT: env.IS_PREVIEW reads from the DFE-specific env var, not the generic VITE_IS_PREVIEW.
  let environment: 'test' | 'preview' | 'production' = 'test';
  let devSampling = {};
  if (env.IS_PREVIEW === 'true') {
    environment = 'preview';
  } else if (import.meta.env.MODE === 'production') {
    // Deployed builds are all built in production mode; ENVIRONMENT (from publish.config.json)
    // tells the dev site apart from prod. Anything other than 'dev' is treated as production.
    if (env.ENVIRONMENT === 'dev') {
      // The dev site reports as 'test' (DFE never sends 'test' otherwise; Sentry only runs on deployed builds).
      // The base config only samples 'production', so report every dev error, no traces.
      environment = 'test';
      devSampling = { sampleRate: 1, tracesSampleRate: 0 };
    } else {
      environment = 'production';
    }
  }

  return getBaseSentryConfig({
    // IMPORTANT: These env vars must match what is set in reusable-build-and-deploy.yml
    dsn: env.SENTRY_DSN,
    release: env.VERSION,
    environment,
    ...devSampling,
    ignoreErrors: ignorePatterns,
    ignoreTransactions: ignorePatterns,
    // DFE has no deny URLs — all endpoints are first-party
    denyUrls: [],
    // DFE does not use session replay
    integrations: [browserTracingIntegration({ instrumentNavigation: true, enableInp: true })],
    // DFE relies on Sentry's built-in sampleRate for error filtering, not manual beforeSend sampling
    beforeSend: (event: ErrorEvent) => tagThirdPartyEvent(event),
    // DFE does not apply the pendo-static span filter
    beforeSendSpan: (span: any) => tagThirdPartySpan(span),
  });
}
